/**
 * Human-readable labels + one-line summaries for the MCP gateway tools that
 * Merlin specialists invoke. Called from ``RavenChat`` to render subagent
 * tool chips with a glanceable description (e.g. "Domain info — test-jvm,
 * stack: es-staging, engine: OpenSearch 3.3") instead of the raw internal
 * tool name + JSON blob.
 *
 * Design principles:
 * - **Client-side**: copy changes are a PR to the UI, not a Merlin redeploy.
 * - **Rule-based**: no LLM on the hot path; deterministic and instant.
 * - **Graceful fallback**: unknown tools strip the ``merlin-xSvcAPIs___``
 *   namespace prefix, and unknown output shapes show "N fields" / "N items".
 *   Nothing ever renders ``undefined`` or throws.
 * - **Raw data preserved**: the existing ``<details>`` panels with full JSON
 *   stay available for debugging; the summary is additive.
 */

// ─── Tool name → friendly label ─────────────────────────────────────────────

const _FRIENDLY_TOOL_NAME: Record<string, string> = {
  // Domain / config introspection (intSvcAPIs namespace)
  'merlin-intSvcAPIs___DescribeDomainsInternal':    'Domain info',
  'merlin-intSvcAPIs___GetElasticsearchDomainConfig': 'Domain config',
  'merlin-intSvcAPIs___GetElasticsearchVersion':    'Engine version',

  // Cluster-level REST calls
  'merlin-esLambda___elasticsearch_client':         'Cluster API',

  // Log analysis
  'merlin-logAnalyzerTool___log_analyzer_tool':     'Log search',

  // Metrics
  'merlin-metricsAPIs___GetMetricData':             'Metric query',
  'merlin-metricsAPIs___GetMetricsAggregations':    'Metric aggregations',

  // Dump / diagnostic flows
  'merlin-dumpAnalysisTool___dump_analysis_tool':   'Dump analysis',
  'merlin-dumpAnalysisTool___list_dumps':           'List dumps',
  'merlin-dumpAnalysisTool___dump_fetch':           'Fetch dumps',
  'dump_fetch':                                     'Fetch dumps',   // short name some gateways emit
};

/**
 * Map an MCP tool name to a human-readable label. Falls back to stripping the
 * ``merlin-xyzSvcAPIs___`` namespace prefix so an unmapped tool still reads
 * as ``GetSomething`` rather than the full internal handle.
 */
export function friendlyToolName(tool: string): string {
  if (!tool) return 'tool';
  if (_FRIENDLY_TOOL_NAME[tool]) return _FRIENDLY_TOOL_NAME[tool];
  // Common shape: ``merlin-<pkg>___<MethodName>``. Drop everything before
  // ``___`` and camel-case-split for readability.
  const parts = tool.split('___');
  const tail = parts.length > 1 ? parts[parts.length - 1] : tool;
  // Insert spaces before capital letters: ``GetElasticsearchDomain`` → ``Get Elasticsearch Domain``.
  return tail.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ─── Output → one-line summary ──────────────────────────────────────────────

/** Attempt to parse ``value`` as JSON if it's a plausible JSON string. */
function _coerceObject(value: unknown): any {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
  try { return JSON.parse(trimmed); } catch { return value; }
}

/**
 * Unwrap common envelope shapes around the real payload. Backend tools often
 * return ``{"GetElasticsearchDomainConfigResponse": {...actual data...}}`` or
 * ``{"response": {...actual data...}}``. Without this the generic fallback
 * ends up rendering the envelope KEY as the summary (e.g. literally the
 * string "GetElasticsearchDomainConfigResponse") instead of the real fields.
 *
 * Only strips a wrapper when it's the SOLE top-level key — we never want to
 * hide sibling fields like ``ResponseMetadata`` / ``status`` from a real
 * multi-field response.
 */
function _unwrapEnvelope(obj: any, depth: number = 0): any {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  if (depth > 4) return obj;  // guard against pathological nesting

  const keys = Object.keys(obj);
  // Keys that mark an envelope layer (their value is the "real" payload).
  // Beyond ``response`` / ``result`` / ``body`` and the generic
  // ``...Response`` / ``...Result`` suffix conventions, add an explicit AWS
  // allowlist for the final wrapper layer you see under
  // ``GetElasticsearchDomainConfigResult.ElasticsearchDomainConfig`` and
  // cousins. Without these, the unwrapper stops one level too shallow and
  // the summary shows the wrapper key name instead of the actual data.
  const _AWS_PAYLOAD_KEYS = new Set([
    'ElasticsearchDomainConfig', 'elasticsearchDomainConfig',
    'DomainConfig', 'domainConfig',
    'DomainStatus', 'domainStatus',
    'DomainStatusList', 'domainStatusList',
  ]);
  const looksLikePayloadKey = (k: string): boolean =>
    k === 'response' || k === 'result' || k === 'body' ||
    /Response$/.test(k) || /Result$/.test(k) ||
    _AWS_PAYLOAD_KEYS.has(k);

  // Case A: single-key envelope → ``{"XxxResponse": {...}}``.
  if (keys.length === 1) {
    const k = keys[0];
    const v = (obj as any)[k];
    if (v && typeof v === 'object' && looksLikePayloadKey(k)) {
      return _unwrapEnvelope(v, depth + 1);  // recurse — inner object may also be wrapped
    }
    return obj;
  }

  // Case B: AWS-style ``{"XxxResult": {...actual...}, "ResponseMetadata": {...boto...}}``
  // Two top-level keys, one is boto boilerplate metadata, the other is the
  // real payload. Strip the metadata and recurse on the payload.
  if (keys.length === 2) {
    const metaKeys = new Set(['ResponseMetadata', 'responseMetadata', 'metadata']);
    const hasMeta = keys.some(k => metaKeys.has(k));
    const payloadKey = keys.find(k => !metaKeys.has(k));
    if (hasMeta && payloadKey) {
      const payload = (obj as any)[payloadKey];
      if (payload && typeof payload === 'object') {
        // If the payload key itself looks like an envelope (``...Result``),
        // peel that too; otherwise return the payload as-is.
        return looksLikePayloadKey(payloadKey)
          ? _unwrapEnvelope(payload, depth + 1)
          : payload;
      }
    }
  }

  return obj;
}

function _fmtCount(n: number, singular: string, plural?: string): string {
  return `${n} ${n === 1 ? singular : (plural ?? `${singular}s`)}`;
}

function _firstString(obj: any, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}

/**
 * Build a short summary line for the tool's output. Tool-specific rules live
 * here; unknown shapes get a generic "N fields" / "N items" fallback. Never
 * exceeds ~140 chars so it fits a single line in the UI.
 */
/**
 * Known shapes that indicate a tool returned a failure rather than a success.
 * Probed on the top-level object AND inside a ``response`` wrapper so we
 * catch both ``{status:"error",...}`` and ``{response:{status:"error",...}}``.
 */
function _looksLikeFailure(obj: any): boolean {
  if (obj == null) return false;
  if (typeof obj !== 'object') return false;
  const probe = (x: any): boolean => {
    if (!x || typeof x !== 'object') return false;
    if (typeof x.status === 'string' && /^(error|fail(ed|ure)?)$/i.test(x.status)) return true;
    if (typeof x.errorType === 'string' && x.errorType) return true;
    if (typeof x.errorReason === 'string' && x.errorReason) return true;
    if (typeof x.failureType === 'string' && x.failureType) return true;
    if ('error' in x && x.error != null && x.error !== '') return true;
    return false;
  };
  return probe(obj) || probe(obj.response);
}

export interface ToolOutputSummary {
  /** One-line human-readable summary (as returned by ``summarizeToolOutput``). */
  text: string;
  /** True if the output shape looks like a failure (status=error, errorReason, etc.) */
  failed: boolean;
}

/**
 * Same as ``summarizeToolOutput`` but also reports whether the tool output
 * looks like a failure. Used by the UI to style the result line red vs green.
 */
export function summarizeToolOutputDetailed(tool: string, output: unknown): ToolOutputSummary {
  // Unwrap the envelope before checking for failure so nested indicators
  // like ``{"GetXxxResponse": {"status": "error"}}`` agree with the text
  // summary below (which also operates on the unwrapped payload).
  const obj = _unwrapEnvelope(_coerceObject(output));
  const failed = _looksLikeFailure(obj);
  return { text: summarizeToolOutput(tool, output), failed };
}

export function summarizeToolOutput(tool: string, output: unknown): string {
  if (output == null || output === '') return '';

  // Peel single-key envelopes (``{"XxxResponse": {...}}``) so tool-specific
  // and generic branches below see the real payload.
  const obj = _unwrapEnvelope(_coerceObject(output));

  // ── Merlin wire-level error wrapper (see stream_projector surfacing rule) ──
  // Note: we return the bare reason; the UI adds the ⚠︎ icon itself when
  // ``outputDetails.failed`` is true. Prepending one here caused a double
  // warning symbol in the rendered line.
  if (obj && typeof obj === 'object' && 'error' in obj) {
    const msg = typeof (obj as any).error === 'string'
      ? (obj as any).error
      : 'tool reported an error';
    return msg.slice(0, 180);
  }

  // ── Generic failure shapes (status=error, errorReason, failureType, …) ──
  // The supervising specialist's response models often have these fields on
  // the tool output when the backend rejected the request (cluster did not
  // exist, throttled, etc.). Prefer a human-readable reason over the
  // generic "N fields" fallback below.
  if (_looksLikeFailure(obj)) {
    const body = (obj?.response && typeof obj.response === 'object' && !Array.isArray(obj.response))
      ? obj.response
      : obj;
    const reason =
      (typeof body?.errorReason === 'string' && body.errorReason) ||
      (typeof body?.errorType   === 'string' && body.errorType)   ||
      (typeof body?.failureType === 'string' && body.failureType) ||
      (typeof body?.error === 'string' && body.error) ||
      (typeof body?.status === 'string' && body.status) ||
      'tool reported a failure';
    return String(reason).slice(0, 180);
  }

  // ── DescribeDomainsInternal / GetElasticsearchDomainConfig ──
  if (tool.endsWith('DescribeDomainsInternal') || tool.endsWith('GetElasticsearchDomainConfig')) {
    // First peel any envelope (``{"GetElasticsearchDomainConfigResponse":
    // {...}}``, ``{"response": {...}}``, etc.). Then dig through the common
    // wrapper shapes inside. Without envelope-unwrap, a single-key envelope
    // ends up rendering its KEY as the summary text, which looks awful
    // (literally "GetElasticsearchDomainConfigResponse").
    const peeled = _unwrapEnvelope(obj);
    const d = peeled?.domain
      ?? peeled?.response?.domain
      ?? (Array.isArray(peeled?.domains) ? peeled.domains[0] : undefined)
      ?? (Array.isArray(peeled?.DomainStatusList) ? peeled.DomainStatusList[0] : undefined)
      ?? peeled?.DomainConfig
      ?? peeled?.domainConfig
      ?? peeled?.domain_config
      ?? (Array.isArray(peeled?.response) ? peeled.response[0] : undefined)
      ?? (typeof peeled?.response === 'object' && peeled.response !== null ? peeled.response : undefined)
      ?? peeled;
    const parts: string[] = [];
    const name = _firstString(d, ['domain', 'domainName', 'DomainName', 'name', 'Name']);
    const stack = _firstString(d, ['stack', 'Stack']);
    const engine = _firstString(d, ['engine', 'engineType', 'Engine', 'EngineType']);
    const version = _firstString(d, ['version', 'engineVersion', 'Version', 'EngineVersion']);
    const region = _firstString(d, ['region', 'awsRegion', 'Region', 'AwsRegion']);
    if (name) parts.push(name);
    if (stack) parts.push(`stack: ${stack}`);
    if (engine && version) parts.push(`${engine} ${version}`);
    else if (engine) parts.push(engine);
    else if (version) parts.push(`v${version}`);
    if (region) parts.push(region);
    if (parts.length) return parts.join(' · ');
  }

  // ── GetElasticsearchVersion ──
  if (tool.endsWith('GetElasticsearchVersion')) {
    const ver = _firstString(obj, ['version', 'engineVersion'])
      ?? _firstString(obj?.response, ['version', 'engineVersion']);
    const engine = _firstString(obj, ['engine', 'engineType'])
      ?? _firstString(obj?.response, ['engine', 'engineType']);
    if (engine && ver) return `${engine} ${ver}`;
    if (ver) return `v${ver}`;
  }

  // ── log_analyzer_tool ──
  if (tool.endsWith('log_analyzer_tool')) {
    const count = obj?.match_count ?? obj?.matchCount ?? obj?.response?.match_count;
    const window = obj?.window ?? obj?.response?.window;
    const err = obj?.error ?? obj?.response?.error;
    if (typeof err === 'string' && err) return `⚠︎ ${err.slice(0, 140)}`;
    if (typeof count === 'number') {
      const matches = _fmtCount(count, 'match', 'matches');
      return window ? `${matches} · ${window}` : matches;
    }
  }

  // ── elasticsearch_client (cluster REST) ──
  if (tool.endsWith('elasticsearch_client')) {
    // The gateway may return the cluster REST body directly OR wrap it in
    // ``{"response": {...}}`` depending on the endpoint. Check both.
    const body = (obj && typeof obj === 'object' && 'response' in obj && typeof (obj as any).response === 'object' && !Array.isArray((obj as any).response))
      ? (obj as any).response
      : obj;

    // _cluster/health: {"status":"green","number_of_nodes":3,...}
    if (body?.status && typeof body.status === 'string') {
      const parts = [`status: ${body.status}`];
      if (typeof body.number_of_nodes === 'number') parts.push(_fmtCount(body.number_of_nodes, 'node'));
      if (typeof body.active_shards === 'number') parts.push(_fmtCount(body.active_shards, 'shard'));
      if (typeof body.cluster_name === 'string') parts.push(body.cluster_name);
      return parts.join(' · ');
    }
    // _cat/* endpoints return a plain array of rows.
    if (Array.isArray(obj?.response)) return _fmtCount(obj.response.length, 'row');
    if (Array.isArray(obj)) return _fmtCount(obj.length, 'row');
  }

  // ── GetMetricData ──
  if (tool.endsWith('GetMetricData')) {
    const series = obj?.metricDataResults ?? obj?.response?.metricDataResults;
    if (Array.isArray(series)) {
      const totalPoints = series.reduce(
        (acc: number, s: any) => acc + (Array.isArray(s?.values) ? s.values.length : 0),
        0,
      );
      return `${_fmtCount(series.length, 'metric')} · ${_fmtCount(totalPoints, 'datapoint')}`;
    }
  }

  // ── GetMetricsAggregations ──
  if (tool.endsWith('GetMetricsAggregations')) {
    const rows = obj?.response ?? obj?.rows ?? obj?.aggregations;
    if (Array.isArray(rows)) return _fmtCount(rows.length, 'row');
  }

  // ── Generic fallbacks ──
  if (Array.isArray(obj)) return _fmtCount(obj.length, 'item');
  if (Array.isArray(obj?.response)) return _fmtCount(obj.response.length, 'item');
  if (obj && typeof obj === 'object') {
    // Prefer key:value pairs (e.g. ``domain: test-jvm · engine: OpenSearch``)
    // over bare key names (``domain, engine``) — dramatically more useful
    // for unmapped tools. Only include primitive values so we don't splat
    // nested objects into the summary.
    const pairs: string[] = [];
    for (const [k, v] of Object.entries(obj)) {
      if (pairs.length >= 4) break;
      if (typeof v === 'string') {
        const trimmed = v.length > 50 ? v.slice(0, 50) + '…' : v;
        if (trimmed) pairs.push(`${k}: ${trimmed}`);
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        pairs.push(`${k}: ${v}`);
      } else if (Array.isArray(v)) {
        pairs.push(`${k}: ${_fmtCount(v.length, 'item')}`);
      }
      // Skip nested objects — showing ``stack: [object Object]`` helps nobody.
    }
    if (pairs.length > 0) return pairs.join(' · ');

    // All values are nested objects (e.g. the ElasticsearchDomainConfig
    // payload with AIMLOptions / AccessPolicies / AdvancedOptions / …).
    // Report "N sections" — rendering the key names would just produce
    // a meaningless CSV.
    const keys = Object.keys(obj);
    const allNested = keys.every(k => {
      const v = (obj as any)[k];
      return v && typeof v === 'object';
    });
    if (allNested && keys.length > 0) {
      return _fmtCount(keys.length, 'section');
    }

    // Otherwise list keys as a last resort.
    if (keys.length > 0 && keys.length <= 8) {
      return keys.slice(0, 5).join(', ');
    }
    if (keys.length > 0) return _fmtCount(keys.length, 'field');
  }

  // Plain string tool output — just truncate.
  if (typeof output === 'string') {
    const one = output.trim().replace(/\s+/g, ' ');
    return one.length > 140 ? one.slice(0, 140) + '…' : one;
  }

  return '';
}

/**
 * Optional: build a short input description. Today only a few tools have
 * inputs worth describing glanceably; the rest fall back to empty so the
 * caller can skip rendering. Mirrors ``summarizeToolOutput`` in shape.
 */
export function summarizeToolInput(tool: string, input: unknown): string {
  if (input == null || input === '') return '';
  const obj = _coerceObject(input);
  if (!obj || typeof obj !== 'object') return '';

  // ── log_analyzer_tool ──
  if (tool.endsWith('log_analyzer_tool')) {
    const ident = obj?.identuple;
    const logP = obj?.logParameters;
    const parts: string[] = [];
    if (ident?.domain) parts.push(ident.domain);
    if (logP?.file_name) parts.push(logP.file_name);
    if (Array.isArray(logP?.search_terms) && logP.search_terms.length) {
      const terms = logP.search_terms.slice(0, 3).join(', ');
      const more = logP.search_terms.length > 3 ? ` +${logP.search_terms.length - 3}` : '';
      parts.push(`[${terms}${more}]`);
    }
    return parts.join(' · ');
  }

  // ── elasticsearch_client ──
  if (tool.endsWith('elasticsearch_client')) {
    const cmd = _firstString(obj, ['command', 'path']);
    if (cmd) return cmd;
  }

  // ── GetMetricData ──
  if (tool.endsWith('GetMetricData')) {
    const queries = obj?.metricDataQueries;
    if (Array.isArray(queries) && queries.length) {
      const names = queries.slice(0, 3).map((q: any) => q?.metricName).filter(Boolean);
      if (names.length === 0) return '';          // no names → the "+N" suffix alone is noise
      const more = queries.length > names.length ? ` +${queries.length - names.length} more` : '';
      return `${names.join(', ')}${more}`;
    }
  }

  return '';
}


// ─── Tool purpose (why is this being called) ───────────────────────────

/**
 * Short description of *why* the specialist called a given tool. Rule-based,
 * deterministic, always produces a line even when the specialist LLM didn't
 * narrate its plan via ``subagent_reasoning``. Helps users follow what's
 * happening when the agent is running many nested calls.
 *
 * For REST-style tools (``elasticsearch_client``) we look at the ``command``
 * path and match ``_cluster/health``, ``_cat/nodes``, etc. individually since
 * each REST endpoint serves a distinct diagnostic purpose.
 */
export function purposeForTool(tool: string, input: unknown): string {
  const obj = _coerceObject(input);

  // ── intSvcAPIs (domain metadata lookups) ──
  if (tool.endsWith('DescribeDomainsInternal')) {
    return 'Looking up domain metadata (stack, cell, engine, version, generation).';
  }
  if (tool.endsWith('GetElasticsearchDomainConfig')) {
    return 'Fetching domain configuration — stack, generation, instance topology.';
  }
  if (tool.endsWith('GetElasticsearchVersion')) {
    return 'Checking which OpenSearch/Elasticsearch version this domain runs.';
  }

  // ── Cluster REST ──
  if (tool.endsWith('elasticsearch_client')) {
    const cmd = (typeof obj === 'object' && obj)
      ? (typeof obj.command === 'string' ? obj.command : (typeof obj.path === 'string' ? obj.path : ''))
      : '';
    if (!cmd) return 'Calling the cluster REST API.';
    // Normalise: drop leading slash, split query string.
    const path = cmd.replace(/^\//, '').split('?')[0];
    if (path.startsWith('_cluster/health')) return 'Checking overall cluster health (status, node count, shard counts).';
    if (path.startsWith('_cluster/state')) return 'Inspecting full cluster state (routing table, metadata).';
    if (path.startsWith('_cluster/settings')) return 'Reading cluster-level settings.';
    if (path.startsWith('_cluster/allocation/explain')) return 'Asking why an unassigned shard is unassigned.';
    if (path.startsWith('_cat/nodes')) return 'Listing nodes, their roles, and resource utilisation.';
    if (path.startsWith('_cat/shards')) return 'Listing shards and their placement across nodes.';
    if (path.startsWith('_cat/indices')) return 'Listing indices with doc counts, storage, and health.';
    if (path.startsWith('_cat/allocation')) return 'Checking per-node shard allocation and disk usage.';
    if (path.startsWith('_cat/pending_tasks')) return 'Looking for queued cluster-state changes.';
    if (path.startsWith('_cat/recovery')) return 'Checking in-progress shard recovery tasks.';
    if (path.startsWith('_cat/thread_pool')) return 'Inspecting thread-pool saturation (search, write, bulk queues).';
    if (path.startsWith('_cat/segments')) return 'Counting segments per shard — proxy for indexing / merge pressure.';
    if (path.startsWith('_cat/master')) return 'Identifying the current master node.';
    if (path.startsWith('_nodes/stats')) return 'Collecting detailed per-node stats (JVM, OS, fs, transport).';
    if (path.startsWith('_nodes') && path.includes('jvm')) return 'Reading JVM heap / GC stats per node.';
    if (path.startsWith('_nodes') && path.includes('os')) return 'Reading OS-level stats (load, memory) per node.';
    if (path.startsWith('_nodes') && path.includes('fs')) return 'Reading filesystem stats per node.';
    if (path.startsWith('_nodes')) return 'Querying node-level metadata.';
    if (path.startsWith('_tasks')) return 'Listing currently-running cluster tasks.';
    if (path.startsWith('_snapshot')) return 'Inspecting snapshot repositories / status.';
    if (path.startsWith('_refresh') || path.endsWith('/_refresh')) return 'Forcing an index refresh.';
    if (path.endsWith('/_search')) return 'Running a search query for diagnostic purposes.';
    return `Calling the cluster REST API: \`${cmd}\`.`;
  }

  // ── Logs ──
  if (tool.endsWith('log_analyzer_tool')) {
    const fn = (typeof obj === 'object' && obj)
      ? (typeof obj.logParameters?.file_name === 'string' ? obj.logParameters.file_name : '')
      : '';
    const terms = (typeof obj === 'object' && obj && Array.isArray(obj.logParameters?.search_terms))
      ? obj.logParameters.search_terms.length
      : 0;
    const detail = [fn, terms ? `${terms} search term${terms === 1 ? '' : 's'}` : '']
      .filter(Boolean)
      .join(' · ');
    return `Searching application logs${detail ? ` (${detail})` : ''} for stability / error patterns.`;
  }

  // ── Metrics ──
  if (tool.endsWith('GetMetricData')) {
    const queries = (typeof obj === 'object' && obj && Array.isArray(obj.metricDataQueries))
      ? obj.metricDataQueries
      : [];
    if (queries.length > 0) {
      const names = queries
        .slice(0, 3)
        .map((q: any) => q?.metricName)
        .filter(Boolean);
      if (names.length > 0) {
        const more = queries.length > names.length ? `, +${queries.length - names.length} more` : '';
        return `Fetching time-series data for ${names.join(', ')}${more}.`;
      }
    }
    return 'Fetching CloudWatch time-series data.';
  }
  if (tool.endsWith('GetMetricsAggregations')) {
    return 'Requesting top-N aggregated metrics (indexes, instance types).';
  }

  // ── Dumps / diagnostics ──
  if (tool.endsWith('list_dumps')) {
    return 'Listing available JVM / profiler dumps for this domain.';
  }
  if (tool.endsWith('dump_analysis_tool')) {
    return 'Analysing a JVM / CPU / allocation dump to identify hotspots.';
  }

  return '';
}


/**
 * Build a deterministic step label for a tool call when the LLM didn't
 * emit a natural-language narration before the tool use. Used as a
 * fallback so every step in the timeline always has a human-readable
 * line (vs showing raw tool names). Capped to ~15 words to match the
 * LLM-side directive.
 */
export function stepLabelForTool(tool: string, input: unknown): string {
  const friendly = friendlyToolName(tool);
  const purpose = purposeForTool(tool, input);
  if (purpose) {
    // Purpose is usually a short imperative; prepend nothing.
    return purpose;
  }
  const inputSummary = summarizeToolInput(tool, input);
  if (inputSummary) {
    return `Calling ${friendly} — ${inputSummary}`;
  }
  return `Calling ${friendly}`;
}
